import KeyboardTabIcon from "@mui/icons-material/KeyboardTab";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MuiDrawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha, styled } from "@mui/material/styles";
import { HttpStatusCode } from "axios";
import { UnavailableReason } from "_shared/_api/by-ids/by-ids-result";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { getApiErrorStatus, getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import DeleteIconButton from "_shared/_views/buttons/delete-icon-button";
import RowLink from "_shared/_views/tables/row-link";
import EntityMember from "_shared/entity-members/entity-member";
import TeamRosterTable, {
  TEAM_LEADER_ROLE,
  TEAM_MEMBER_ROLE,
  TEAM_ROSTER_PERSON_COLUMNS,
  teamRosterCell,
  type TeamRosterCell,
  type TeamRosterRow,
} from "_shared/entity-members/_views/team-roster-table";
import Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import Individual from "marketplace/individuals/individual";
import { unavailableProfileText } from "marketplace/individuals/individual-unavailability-text";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { useCallback, useMemo, useState } from "react";
import { useNavigate, type NavigateFunction } from "react-router";
import { useReplaceVendorTeamMembersMutation } from "work/work-agreements/_api/work-agreement-api";
import VendorTeamDeparture from "work/work-agreements/teams/vendor-team/vendor-team-departure";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";
import vendorTeamDepartureRows from "work/work-agreements/teams/vendor-team/_views/vendor-team-departure-rows";
import VendorTeamMemberSelection from "work/work-agreements/teams/vendor-team/_views/members/vendor-team-member-selection";
import { ratesStruckThroughWarning } from "work/work-agreements/teams/_views/rates-struck-through-warning";
import type WorkAgreement from "work/work-agreements/work-agreement";
import { shellTopBannersHeight } from "_shared/_views/shell-top-banners";

const MainContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
}));
const TeamContainer = styled(Box)(({ theme }) => ({
  columnGap: theme.spacing(10),
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-start",
  paddingBottom: theme.spacing(3),
  rowGap: theme.spacing(3),
  width: "100%",
  [theme.breakpoints.down("lg")]: {
    columnGap: theme.spacing(3),
  },
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    paddingBottom: 0,
  },
}));
const memberSelectionPanelWidth = "min(500px, 100vw)";
const MemberSelectionDrawer = styled(MuiDrawer)(({ theme }) => ({
  flexShrink: 0,
  "& .MuiDrawer-paper": {
    boxSizing: "border-box",
    height: `calc(100% - 64px - ${shellTopBannersHeight()})`,
    maxWidth: "100vw",
    overflow: "hidden",
    top: `calc(64px + ${shellTopBannersHeight()})`,
    width: memberSelectionPanelWidth,
    // The app bar is 64px from `sm` up and 56px below it (`headers/header.tsx`).
    [theme.breakpoints.down("sm")]: {
      height: `calc(100% - ${theme.spacing(7)} - ${shellTopBannersHeight()})`,
      top: `calc(${theme.spacing(7)} + ${shellTopBannersHeight()})`,
    },
  },
}));
const MemberSelectionPanel = styled(MemberSelectionDrawer, {
  shouldForwardProp: (prop) => prop !== "closeAfterTransition",
})<{ open?: boolean }>(({ theme, open }) => ({
  "& .MuiDrawer-paper": {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: open ? theme.transitions.easing.easeOut : theme.transitions.easing.sharp,
      duration: open ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
    }),
    width: open ? memberSelectionPanelWidth : 0,
  },
}));
const MemberSelectionPanelContainer = styled("div")(() => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  height: "100%",
  maxWidth: "100vw",
  minHeight: 0,
  width: memberSelectionPanelWidth,
}));
const MemberSelectionPanelTitleBar = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  padding: theme.spacing(2, 2, 1),
}));
const MemberSelectionPanelContent = styled("div")(() => ({
  display: "flex",
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
}));
const MemberSelectionPanelActions = styled("div")(({ theme }) => ({
  backgroundColor: "#FAFAFA",
  padding: theme.spacing(1.5, 2, 10),
}));
const MemberSelectionCancelButton = styled(Button)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.04),
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
}));

/**
 * What a row says when the directory will not name the person on it. A membership is two GUIDs, and
 * a GUID is not something to show a reader — an unresolved row is described, not identified (#4830).
 */
const UNNAMED_VENDOR_TEAM_MEMBER = "Vendor team member";
/** Room for the one control a row here carries: remove. */
const VENDOR_ROSTER_ACTIONS_COLUMN_WIDTH = 90;
/** Why Remove is refused while the last change is still being written. */
const ROSTER_BUSY_REASON = "Waiting for the last roster change to be saved";

type VendorTeamRestaffingViewProps = {
  className?: string;
  /** The agreement whose roster this is. The command is addressed to it, not to the team. */
  workAgreementId: Guid;
  version: number;
  refreshWorkAgreement: () => unknown;
  /** The roster as the agreement currently holds it — the only source of truth this view draws. */
  team: VendorTeam;
  /** The people behind the roster's memberships, already resolved by the tab that owns the query. */
  individuals: readonly Individual[];
  /**
   * Why the directory did not return a member, by user id, when it said so: `missing` or
   * `unauthorized`. A member it has not answered for is described in general terms.
   */
  unavailability?: Readonly<Record<string, UnavailableReason>>;
  /** Everyone who has left this vendor team, and why each one left (#5663). */
  departures?: readonly VendorTeamDeparture[];
  /** The agreement, so removing somebody a rate names says what becomes of the rate (#6096). */
  workAgreement?: WorkAgreement;
};

/**
 * The vendor team of live, unrestricted work, as its leader re-staffs it.
 *
 * The third of the vendor team's three rosters, beside the draft's `vendor-team-edit-view` and the
 * frozen `vendor-team-readonly-view`. It is the Client Team tab's shape rather than the draft's:
 * the leader is fixed, each member row carries Remove, and Add Member sits in the table's own add
 * control. Nothing is staged — a press is a command, the way removing a client team member is —
 * because there is no Save on this page to press afterwards and a roster half-changed in the
 * browser is a roster nobody is actually staffed on.
 *
 * It holds no copy of the roster either. The agreement is what the page reads the team from, and the
 * command invalidates it, so the roster that comes back from the server is the roster that is drawn.
 * An optimistic list kept here would be a second answer able to disagree with the first — and this
 * command has an answer that is not a roster at all (see {@link commitVendorTeamRoster}).
 */
export default function VendorTeamRestaffingView(props: Readonly<VendorTeamRestaffingViewProps>) {
  const { className, individuals, team, workAgreementId, version, refreshWorkAgreement, unavailability, workAgreement } = props;
  const departures = props.departures ?? [];

  const { openDialog } = useDialog();
  const confirm = useConfirmDialog();
  const navigate = useNavigate();
  const [replaceMembers, replaceMembersResult] = useReplaceVendorTeamMembersMutation();
  const [memberSelectionOpen, setMemberSelectionOpen] = useState(false);

  const leader = team.leader;
  // A person leads a vendor team or belongs to it, never both — `VendorTeam.isLedBy` is the domain's
  // own reading of that, and the API rejects a roster that names the leader among its members.
  const members = useMemo(() => team.members.filter((member) => !team.isLedBy(member)), [team]);
  const individualsByUserId = useMemo(
    () => new Map(individuals.map((individual) => [individual.userId.value, individual])),
    [individuals],
  );
  // One command at a time: the roster sent is the whole roster, so a second press while the first is
  // in flight would be composed from a list the server has already moved past.
  const rosterIsCommitting = replaceMembersResult.isLoading;

  const replaceRoster = useCallback(
    async (nextMembers: EntityMember[], changedMessage: string) => {
      await commitVendorTeamRoster(replaceMembers, {
        changedMessage,
        members: nextMembers,
        navigate,
        refreshWorkAgreement,
        version,
        workAgreementId: workAgreementId.value,
      });
    },
    [navigate, refreshWorkAgreement, replaceMembers, version, workAgreementId],
  );

  const addMember = useCallback(
    async (individual: Individual) => {
      const member = individual.toEntityMember();
      if (!member.userId || !member.legalEntityId) return;
      if (member.userId.isEqualTo(leader?.userId)) return;
      if (members.some((existing) => existing.isEqualTo(member))) return;
      await replaceRoster([...members, member], `${individual.fullName} joined the vendor team.`);
    },
    [leader?.userId, members, replaceRoster],
  );

  const removeMember = useCallback(
    async (member: EntityMember, displayName: string) => {
      // Removing somebody from live work takes away the work and its documents with them, so it is
      // confirmed rather than simply done.
      const rateWarning = workAgreement && member.userId ? ratesStruckThroughWarning(workAgreement, member.userId) : "";
      const response = await confirm({
        title: "Remove vendor team member?",
        message: `${displayName} will lose access to this work agreement and its documents.${rateWarning}`,
        okButtonText: "Remove",
      });
      if (response === ConfirmResponse.Cancel) return;
      await replaceRoster(
        members.filter((item) => !item.isEqualTo(member)),
        `${displayName} left the vendor team.`,
      );
    },
    [confirm, members, replaceRoster, workAgreement],
  );

  const openMemberSelector = useCallback(() => setMemberSelectionOpen(true), []);
  const closeMemberSelector = useCallback(() => setMemberSelectionOpen(false), []);
  const openUserProfileDialog = useCallback(
    (individualId: Guid) => {
      openDialog({
        component: <ViewIndividualProfile individualId={individualId} />,
        titleStyle: {
          paddingBottom: 0,
        },
        contentSxProps: {
          display: "flex",
          overflowX: "hidden",
        },
        MuiProps: {
          maxWidth: "lg",
          fullWidth: true,
        },
      });
    },
    [openDialog],
  );

  /**
   * One person's row, drawn from the resolved individual rather than from the membership behind it.
   *
   * `onRemove` is the row's only control, and the leader's row has none: who speaks for the vendor
   * is a term of the agreement, and the route to a different one is `revise`.
   */
  function rosterRow(member: EntityMember, role: string, onRemove?: () => void): TeamRosterRow {
    const individual = individualsByUserId.get(member.userId.value);
    const unavailable = individual ? undefined : unavailability?.[member.userId.value];
    const displayName = individual?.fullName ?? UNNAMED_VENDOR_TEAM_MEMBER;
    return {
      id: `${member.userId.value}:${member.legalEntityId.value}`,
      searchText: rosterSearchText(individual, unavailable),
      sortText: displayName,
      avatar: individual ? <IndividualAvatar individual={individual} /> : <Avatar />,
      name: memberNameCell(individual, openUserProfileDialog),
      email: memberEmailCell(individual, unavailable),
      company: teamRosterCell(individual?.entityName),
      role: { content: role },
      actions: onRemove ? (
        // The accessible name says which row it is on: a column of buttons all called "Remove" tells
        // a screen reader nothing about who is about to lose the work.
        <Tooltip title={rosterIsCommitting ? ROSTER_BUSY_REASON : `Remove ${displayName} from the vendor team`}>
          <span>
            <DeleteIconButton
              aria-label={`Remove ${displayName} from the vendor team`}
              disabled={rosterIsCommitting}
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                onRemove();
              }}
            >
              <DeleteOutlinedIcon />
            </DeleteIconButton>
          </span>
        </Tooltip>
      ) : undefined,
      // One action, so a phone keeps it a button and sizes the column to it — see `TeamRosterRow`.
      menuActions: onRemove
        ? [
          {
            key: "remove",
            label: "Remove",
            ariaLabel: `Remove ${displayName} from the vendor team`,
            icon: <DeleteOutlinedIcon />,
            destructive: true,
            disabled: rosterIsCommitting,
            disabledReason: ROSTER_BUSY_REASON,
            onClick: () => onRemove(),
          },
        ]
        : undefined,
    };
  }

  const rosterRows: TeamRosterRow[] = [
    ...(leader ? [rosterRow(leader, TEAM_LEADER_ROLE)] : []),
    ...members.map((member) =>
      rosterRow(member, TEAM_MEMBER_ROLE, () =>
        removeMember(member, individualsByUserId.get(member.userId.value)?.fullName ?? UNNAMED_VENDOR_TEAM_MEMBER),
      ),
    ),
    // Whoever is gone reads after whoever is still here, and carries no control: a leader can put
    // somebody back on through Add Member, and nothing else about a departure is theirs to change.
    ...vendorTeamDepartureRows({ departures, individuals }),
  ];
  const selectedTeamMemberUserIds = [leader?.userId, ...members.map((member) => member.userId)].filter(
    Boolean,
  ) as Guid[];

  return (
    <MainContainer className={className}>
      <TeamContainer>
        <TeamRosterTable
          title="Team Roster"
          surface="work-agreement-vendor-team"
          columns={TEAM_ROSTER_PERSON_COLUMNS}
          rows={rosterRows}
          emptyMessage="No Team Members"
          noMatchesMessage="No matching team members"
          // This roster carries controls, so it offers the search the frozen one deliberately does
          // not: a leader looking for the person to take off a long team has something to search for.
          enableSearch
          // One control per row rather than the active proposal's five, which would park an empty
          // pinned column over the Role cell.
          actionsColumnWidth={VENDOR_ROSTER_ACTIONS_COLUMN_WIDTH}
          // One way to add somebody, so the table draws one ordinary button rather than a split one.
          addActions={[{ label: "Add Member", onClick: openMemberSelector, disabled: rosterIsCommitting }]}
        />
      </TeamContainer>
      <MemberSelectionPanel open={memberSelectionOpen} anchor="right" variant="persistent" closeAfterTransition={false}>
        <MemberSelectionPanelContainer>
          <MemberSelectionPanelTitleBar>
            <Typography variant="h5">Add vendor team member</Typography>
            <Tooltip title="Minimize panel">
              <IconButton size="medium" onClick={closeMemberSelector}>
                <KeyboardTabIcon fontSize="large" />
              </IconButton>
            </Tooltip>
          </MemberSelectionPanelTitleBar>
          <MemberSelectionPanelContent>
            <VendorTeamMemberSelection
              selectLeader={false}
              selectMember
              initialActiveTab="coworkers"
              selectedTeamMemberUserIds={selectedTeamMemberUserIds}
              onNewSelection={addMember}
              closeDialog={closeMemberSelector}
            />
          </MemberSelectionPanelContent>
          <MemberSelectionPanelActions>
            <MemberSelectionCancelButton fullWidth variant="outlined" color="primary" onClick={closeMemberSelector}>
              Cancel
            </MemberSelectionCancelButton>
          </MemberSelectionPanelActions>
        </MemberSelectionPanelContainer>
      </MemberSelectionPanel>
    </MainContainer>
  );
}

/** A roster row's name line: a link to a profile, plain text for a contact (#5538). */
function memberNameCell(individual: Individual | undefined, onViewProfile: (individualId: Guid) => void) {
  if (!individual) return <Typography>{UNNAMED_VENDOR_TEAM_MEMBER}</Typography>;
  if (individual.contactOnly) return <Typography>{individual.fullName}</Typography>;
  return (
    <RowLink ariaLabel={individual.fullName} onClick={() => onViewProfile(individual.id)}>
      {individual.fullName}
    </RowLink>
  );
}

function memberSecondaryText(individual?: Individual, unavailable?: UnavailableReason): string | undefined {
  if (individual) return individual.email?.value;
  return unavailableProfileText(unavailable);
}

function memberEmailCell(individual?: Individual, unavailable?: UnavailableReason): TeamRosterCell {
  return teamRosterCell(memberSecondaryText(individual, unavailable));
}

function rosterSearchText(individual?: Individual, unavailable?: UnavailableReason): string {
  const name = individual?.fullName ?? UNNAMED_VENDOR_TEAM_MEMBER;
  const secondary = memberSecondaryText(individual, unavailable) ?? "";
  return `${name} ${secondary}`.trim();
}

type RosterChangeOutcome = "changed" | "revised" | "refused";

/**
 * Sends one roster replacement and answers what the server did with it.
 *
 * The command has two answers and they are different events, not two spellings of success. Nothing
 * back means the live roster changed, and the page says so quietly. A proposal back means the change
 * turned out to touch an agreed term after all — the team was restricted on running work by the time
 * the command arrived — so a revision was opened instead, and the reader is told that and taken to
 * it rather than left believing the team moved. Taking off somebody a rate names is not such a
 * change: the rate is struck through, and the work goes on (#6096). That is
 * the same thing the Revise action does with the same answer.
 *
 * At module scope because it owns a `try`, which no component body should (see app/CLAUDE.md).
 */
async function commitVendorTeamRoster(
  replaceMembers: ReturnType<typeof useReplaceVendorTeamMembersMutation>[0],
  request: {
    workAgreementId: string;
    version: number;
    members: EntityMember[];
    changedMessage: string;
    navigate: NavigateFunction;
    refreshWorkAgreement: () => unknown;
  },
): Promise<RosterChangeOutcome> {
  try {
    const revision = await replaceMembers({
      workAgreementId: request.workAgreementId,
      version: request.version,
      members: request.members,
    }).unwrap();
    if (!revision?.id) {
      enqueueSnackbar(request.changedMessage, { variant: "success" });
      return "changed";
    }
    enqueueSnackbar("This change touches the agreed terms, so it opened a revision to negotiate", {
      variant: "info",
    });
    request.navigate(`/proposals/active/revise/${revision.id}/details`, { replace: true });
    return "revised";
  } catch (failure) {
    const status = getApiErrorStatus(failure);
    if (status === HttpStatusCode.Conflict || status === HttpStatusCode.PreconditionFailed) {
      await request.refreshWorkAgreement();
      enqueueSnackbar(
        status === HttpStatusCode.PreconditionFailed
          ? "The work agreement changed. It has been refreshed. Review the roster before making another change."
          : "The work agreement state changed. It has been refreshed. Review the roster before making another change.",
        { variant: "warning" },
      );
      return "refused";
    }
    enqueueSnackbar(vendorTeamChangeFailureMessage(failure), { variant: "error" });
    return "refused";
  }
}

/**
 * What to say about a refused roster change.
 *
 * The endpoint documents three refusals and they mean different things to the person reading them:
 * a `403` says this is not their roster to staff, a `404` says the agreement is gone, and a `400`
 * says the roster itself was rejected. A single "something went wrong" would leave a leader pressing
 * Remove again at a team they are no longer the leader of.
 *
 * The two named here are the two the shared reader cannot say for itself. A `400` from this endpoint
 * is the domain's validation as `ModelState`, whose text is legacy and untrusted, so the reader
 * declines to repeat it and the roster falls back to the plain refusal — while a `400` that does
 * arrive as Problem Details is repeated word for word.
 */
function vendorTeamChangeFailureMessage(failure: unknown): string {
  const status = getApiErrorStatus(failure);
  if (status === HttpStatusCode.Forbidden) return "Only the vendor team leader can change this team.";
  if (status === HttpStatusCode.NotFound) return "This work agreement could not be found.";
  return getErrorMessageFromUnknown(failure) ?? "Unable to change the vendor team. Refresh and try again.";
}
